import { Button } from "@/components/ui/button";
import { useApproveVoucher } from "@/hooks/use-voucher-actions";
import { useCreateUpsell } from "@/hooks/use-upsells";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { DBVoucher } from "@/lib/db-types";
import type { BudgetLineRow } from "@/hooks/use-project-detail";
import { formatNOK } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";

export default function VoucherApprovalDialog({
  voucherId,
  voucher,
  budgetLines,
  projectId,
  approvingId,
  selectedBudgetLine,
  ekstraAmount,
  approveComment,
  rejectingId,
  rejectComment,
  onSetApprovingId,
  onSetSelectedBudgetLine,
  onSetEkstraAmount,
  onSetApproveComment,
  onSetRejectingId,
  onSetRejectComment,
}: {
  voucherId: string;
  voucher: DBVoucher;
  budgetLines: BudgetLineRow[];
  projectId: string;
  approvingId: string | null;
  selectedBudgetLine: string;
  ekstraAmount: string;
  approveComment: string;
  rejectingId: string | null;
  rejectComment: string;
  onSetApprovingId: (id: string | null) => void;
  onSetSelectedBudgetLine: (line: string) => void;
  onSetEkstraAmount: (amount: string) => void;
  onSetApproveComment: (comment: string) => void;
  onSetRejectingId: (id: string | null) => void;
  onSetRejectComment: (comment: string) => void;
}) {
  const { activeUser } = useImpersonation();
  const approveMut = useApproveVoucher();
  const createUpsellMut = useCreateUpsell();

  // Bilaget er en pending_approval-rad fra /VoucherApproval-køen → skal
  // POSTes til PO og forwardes til daglig leder. Legacy approved-rader
  // (fra /AccountTransactions) skal kun ha lokal budgetlinje-kobling.
  const isPendingApproval = voucher.status === "pending_approval";

  const handleApprove = async () => {
    if (!selectedBudgetLine || !activeUser) return;
    try {
      // Mersalg: opprett upsell-rad og approve uten budget_line_id
      if (selectedBudgetLine === "__ekstra__") {
        const agreedAmount = parseFloat(ekstraAmount);
        await createUpsellMut.mutateAsync({
          project_id: projectId,
          description: voucher.supplier_name
            ? `Mersalg: ${voucher.supplier_name} – ${voucher.description ?? ""}`
            : `Mersalg fra bilag #${voucher.voucher_no}`,
          amount_excl_vat: isNaN(agreedAmount) || agreedAmount <= 0 ? (voucher.amount ?? 0) : agreedAmount,
          category: "bilag",
          source_voucher_id: voucherId,
        });
        await approveMut.mutateAsync({
          voucher_id: voucherId,
          decision: "approved",
          // For pending_approval må budget_line_id være satt — bruk
          // upsell-koblingen via en sentinel-id er ikke trivielt, så
          // fallbacker vi til legacy-flyt: edge-funksjonen aksepterer null
          // for legacy approved, men ikke for pending_approval. Mersalg
          // for nye bilag er sjelden, og vi krever en budget_line_id.
          budget_line_id: null,
          comment: approveComment.trim() || undefined,
          user_id: activeUser.id,
          project_id: projectId,
        });
      } else if (selectedBudgetLine === "__annet__") {
        await approveMut.mutateAsync({
          voucher_id: voucherId,
          decision: "approved",
          budget_line_id: null,
          comment: approveComment.trim() || undefined,
          user_id: activeUser.id,
          project_id: projectId,
        });
      } else {
        await approveMut.mutateAsync({
          voucher_id: voucherId,
          decision: "approved",
          budget_line_id: selectedBudgetLine,
          comment: approveComment.trim() || undefined,
          user_id: activeUser.id,
          project_id: projectId,
        });
      }
      onSetApprovingId(null);
      onSetSelectedBudgetLine("");
      onSetEkstraAmount("");
      onSetApproveComment("");
    } catch (err) {
      console.error("Approval failed:", err);
    }
  };

  const handleReject = async () => {
    if (!activeUser) return;
    if (!rejectComment.trim()) return;
    try {
      await approveMut.mutateAsync({
        voucher_id: voucherId,
        decision: "rejected",
        comment: rejectComment.trim(),
        user_id: activeUser.id,
        project_id: projectId,
      });
      onSetRejectingId(null);
      onSetRejectComment("");
    } catch (err) {
      console.error("Rejection failed:", err);
    }
  };

  const isApproving = approvingId === voucherId;
  const isRejecting = rejectingId === voucherId;

  if (!isApproving && !isRejecting) return null;

  if (isApproving) {
    return (
      <div className="px-4 py-3 bg-green-50/50 border-t border-green-100 space-y-2">
        <label htmlFor={`voucher-budget-line-${voucherId}`} className="block text-xs font-medium text-green-800">
          Knytt til budsjettpost:
        </label>
        <select
          // Panelet erstatter ingenting i DOM, så fokus ble stående på Godkjenn-knappen (VIB-2536).
          autoFocus
          id={`voucher-budget-line-${voucherId}`}
          className="w-full text-sm border rounded-md px-2 py-1.5 bg-white"
          value={selectedBudgetLine}
          onChange={(e) => {
            onSetSelectedBudgetLine(e.target.value);
            onSetEkstraAmount("");
          }}
        >
          <option value="">Velg budsjettlinje...</option>
          {budgetLines.map((bl) => (
            <option key={bl.id} value={bl.id}>
              {bl.phase ? `${bl.phase} – ` : ""}
              {bl.description ?? "Uten beskrivelse"} ({formatNOK(bl.amount)})
            </option>
          ))}
          <option disabled>──────────</option>
          <option value="__annet__">Annet (generell kostnad)</option>
          <option value="__ekstra__">Ekstra (opprett mersalg)</option>
        </select>
        {selectedBudgetLine === "__ekstra__" && (
          <div className="space-y-1">
            <label htmlFor={`voucher-ekstra-${voucherId}`} className="text-xs text-green-700">
              Avtalt pris eks. mva (valgfritt, bruker bilagsbeløp om tomt):
            </label>
            <input
              id={`voucher-ekstra-${voucherId}`}
              type="number"
              className="w-full text-sm border rounded-md px-2 py-1.5 bg-white"
              placeholder={`${voucher.amount}`}
              value={ekstraAmount}
              onChange={(e) => onSetEkstraAmount(e.target.value)}
            />
          </div>
        )}
        {isPendingApproval && (
          <div className="space-y-1">
            <label htmlFor={`voucher-comment-${voucherId}`} className="text-xs text-green-700">
              Kommentar til daglig leder (valgfritt):
            </label>
            <input
              id={`voucher-comment-${voucherId}`}
              type="text"
              className="w-full text-sm border rounded-md px-2 py-1.5 bg-white"
              placeholder="F.eks. forklaring eller begrunnelse"
              value={approveComment}
              onChange={(e) => onSetApproveComment(e.target.value)}
            />
            <p className="text-[11px] text-green-700/80">
              Bilaget sendes til daglig leder med navn, dato og budsjettlinje.
            </p>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => onSetApprovingId(null)}
          >
            Avbryt
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs bg-green-600 hover:bg-green-700"
            disabled={!selectedBudgetLine || approveMut.isPending || createUpsellMut.isPending}
            onClick={handleApprove}
          >
            {approveMut.isPending ? <SrIcon name="loader2" className="w-3 h-3 animate-spin mr-1"  /> : <SrIcon name="check" className="w-3 h-3 mr-1"  />}
            {isPendingApproval ? "Godkjenn og send til DL" : "Godkjenn"}
          </Button>
        </div>
      </div>
    );
  }

  if (isRejecting) {
    return (
      <div className="px-4 py-3 bg-red-50/50 border-t border-red-100 space-y-2">
        <p className="text-xs font-medium text-red-800">
          Begrunnelse{isPendingApproval ? " (påkrevd)" : ""}:
        </p>
        <input
          type="text"
          className="w-full text-sm border rounded-md px-2 py-1.5"
          placeholder="Grunn for avvisning..."
          value={rejectComment}
          onChange={(e) => onSetRejectComment(e.target.value)}
        />
        {isPendingApproval && (
          <p className="text-[11px] text-red-700/80">
            Bilaget returneres til regnskapsfører i Go med begrunnelsen.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onSetRejectingId(null)}>
            Avbryt
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white"
            disabled={approveMut.isPending || (isPendingApproval && !rejectComment.trim())}
            onClick={handleReject}
          >
            {approveMut.isPending ? <SrIcon name="loader2" className="w-3 h-3 animate-spin mr-1"  /> : <SrIcon name="x" className="w-3 h-3 mr-1"  />}
            Avvis
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
